<template>
    <div v-if="isLoaded">
        <div class="page" v-if="order.products.length === 0">
            <h1>Niciun produs în comandă</h1>
            <p>
                Nu aveți niciun produs adăugat în această comandă. Puteți utiliza funcția de căutare pentru
                a găsi produsul dorit și a-l adăuga în comandă.
            </p>

            <div class="mt-2">
                <button class="btn btn-danger btn-success" @click="deleteOrder">
                    <b-icon icon="trash"/>
                    Șterge comanda
                </button>
            </div>
        </div>

        <div v-else>
            <div v-if="hasIssues" class="red page">
                <h1>Probleme de integritate</h1>

                <p>
                    Au fost găsite probleme de integritate la această comandă.
                    Acest lucru înseamnă că informațiile de mai jos nu mai sunt de actualitate.
                </p>

                <p>
                    Vă rugăm să luați la cunoștință aceste erori, și în cazul în care doriți, să le remediați mai jos.
                </p>

                <template v-if="this.issues.changed_order_items.length > 0">
                    <hr />

                    <h2>Informații de stoc actualizate</h2>
                    <p>
                        Au fost detectate schimbări ale informațiilor de stoc utilizate în cadrul acestei comenzi:
                    </p>

                    <table class="table table-sm table-borderless table-striped">
                        <thead>
                        <tr>
                            <th scope="col">Denumire produs</th>
                            <th scope="col">Furnizor</th>
                            <th scope="col" class="nowrap">Preț vechi</th>
                            <th scope="col" class="nowrap">Preț nou</th>
                            <th scope="col" class="nowrap">Stoc vechi</th>
                            <th scope="col" class="nowrap">Stoc nou</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(info, index) in this.issues.changed_order_items" :key="index">
                            <td>{{info.product_name}}</td>
                            <td>{{info.supplier_name}}</td>

                            <template v-if="(info.old_product_stock.price != info.new_product_stock.price) || (info.old_product_stock.currency != info.new_product_stock.currency)">
                                <td class="nowrap">{{parseFloat(info.old_product_stock.price).toFixed(2)}} {{info.old_product_stock.currency}}</td>
                                <td class="nowrap">{{parseFloat(info.new_product_stock.price).toFixed(2)}} {{info.new_product_stock.currency}}</td>
                            </template>
                            <template v-else-if="info.old_product_stock.ron_price != info.new_product_stock.ron_price">
                                <td class="nowrap">{{parseFloat(info.old_product_stock.ron_price).toFixed(2)}} RON</td>
                                <td class="nowrap">{{parseFloat(info.new_product_stock.ron_price).toFixed(2)}} RON</td>
                            </template>
                            <template v-else>
                                <td class="nowrap"></td>
                                <td class="nowrap"></td>
                            </template>

                            <template v-if="info.old_product_stock.quantity != info.new_product_stock.quantity">
                                <td class="nowrap">{{parseFloat(info.old_product_stock.quantity).toFixed(0)}}</td>
                                <td class="nowrap">{{parseFloat(info.new_product_stock.quantity).toFixed(0)}}</td>
                            </template>
                            <template v-else>
                                <td class="nowrap"></td>
                                <td class="nowrap"></td>
                            </template>
                        </tr>
                        </tbody>
                    </table>
                </template>

                <template v-if="this.issues.unavailable_order_items.length > 0">
                    <hr />

                    <h2>Informații de stoc radiate</h2>
                    <p>
                        Au fost detectate ștergeri ale informațiilor de stoc utilizate în cadrul acestei comenzi.
                        Nu s-au putut găsi automat versiuni mai noi ale acestor informații.
                    </p>
                    <p>Produse la care s-au constatat astfel de erori:</p>
                    <ul>
                        <li v-for="(info, index) in this.issues.unavailable_order_items" :key="index">
                            <strong>{{info.product_name}}</strong>
                        </li>
                    </ul>
                </template>

                <h1>Remediere probleme</h1>
                <p>
                    Puteți încerca să remediați automat problemele utilizând butonul de mai jos. Țineți cont că
                    anumite probleme, în speță informațiile de stoc radiate, nu pot fi reparate automat.
                </p>
                <b-button class="mt-2" variant="outline-light" @click="fixIssues">Remediere automată</b-button>
            </div>

            <div class="page" v-for="supplier in this.order.suppliers" :key="supplier.supplier_id">
                <h1>{{ supplier.supplier_name }}</h1>

                <table class="table table-sm table-borderless table-striped">
                    <thead>
                    <tr>
                        <th scope="col">Denumire</th>
                        <th scope="col"></th>
                        <th scope="col">Producător</th>
                        <th scope="col">Preț / buc.</th>
                        <th scope="col">Cantitate</th>
                        <th scope="col">Preț final</th>
                        <th scope="col"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(product, index) in getSupplierProducts(supplier.supplier_id)" :key="product.item_id">
                        <td>
                            {{product.name}}
                            <br/>
                            <div class="product-codes">
                                <span v-if="product.codes.product_info.ean">EAN: {{product.codes.product_info.ean}}</span>
                                <span v-if="product.codes.product_info.upc">UPC: {{product.codes.product_info.upc}}</span>
                                <span v-if="product.codes.product_info.oem_codes">
                                <span>OEM: </span>
                                <span v-for="code in product.codes.product_info.oem_codes" :key="code.id">
                                    {{code.oem_code}}
                                </span>
                            </span>
                                <span v-for="code in product.codes.extra_ids" :key="code.value">{{code.id_name}}: {{code.value}}</span>
                            </div>
                        </td>
                        <td>
                            <ItemNameEditor
                                    :default-name="product.default_name"
                                    :custom-name="product.name"
                                    @saveName="editedName => onItemSaveName(product.item_id, editedName)"
                            />
                        </td>
                        <td>{{product.manufacturer}}</td>
                        <td class="nowrap">
                            <span>{{parseFloat(product.supplier_price).toFixed(2)}} {{product.supplier_currency}}</span><br/>
                            <span class="small-text">{{parseFloat(product.base_ron_price).toFixed(2)}} RON</span>
                        </td>
                        <td class="nowrap">
                            <QuantityPicker
                                    :item-id="product.item_id"
                                    :initial-quantity="product.quantity"
                                    @onSelectQuantity="selectItemQuantity"
                            />

                            <template v-if="product.quantity <= product.supplier_stock">
                                <span class="small-text" style="color: darkgreen;">Stoc: {{ parseFloat(product.supplier_stock).toFixed(0) }} buc.</span>
                            </template>
                            <template v-else>
                                <span class="small-text" style="color: darkred;"><strong>Stoc: {{ parseFloat(product.supplier_stock).toFixed(0) }} buc.</strong></span>
                            </template>
                        </td>

                        <td class="nowrap">
                            <span>{{(product.supplier_price * product.quantity).toFixed(2)}} {{product.supplier_currency}}</span><br>
                        </td>

                        <td class="nowrap">
                            <b-button
                                    variant="success"
                                    size="sm"
                                    class="mr-1"
                                    :disabled="index === 0"
                                    @click="() => moveItemUp(product.item_id, supplier.supplier_id)"
                            >
                                <b-icon icon="arrow-up"/>
                            </b-button>
                            <b-button
                                    variant="success"
                                    size="sm"
                                    class="mr-2"
                                    :disabled="index === getSupplierProducts(supplier.supplier_id).length - 1"
                                    @click="() => moveItemDown(product.item_id, supplier.supplier_id)"
                            >
                                <b-icon icon="arrow-down"/>
                            </b-button>

                            <b-button
                                    variant="danger"
                                    size="sm"
                                    @click="() => deleteItem(product.item_id)"
                            >
                                <b-icon icon="trash"/>
                            </b-button>
                        </td>
                    </tr>

                    </tbody>
                </table>


                <div class="total" v-for="(total, currency) in supplier.supplier_totals" :key="currency">
                    <span class="mr-3">Total <strong>{{ currency }}</strong>:</span>
                    <span class="amount">
                        {{parseFloat(total).toFixed(2)}} {{ currency }}
                    </span>
                </div>
            </div>

            <div class="page">
                <h1>Finalizează comanda</h1>

                <p>
                    Când ați terminat de editat comanda, apăsați butonul de salvare pentru a merge la secțiunea
                    de descărcare a fișierelor individuale de comandă.
                </p>

                <div class="d-flex justify-content-between mt-2">
                    <button class="btn btn-danger btn-success" @click="deleteOrder">
                        <b-icon icon="trash"/>
                        Șterge comanda
                    </button>

                    <button class="btn btn-primary btn-success" @click="saveOrder">
                        <b-icon icon="check2-square"/>
                        Salvează comanda
                    </button>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
    import QuantityPicker from "@/components/QuantityPicker";
    import ItemNameEditor from "@/components/ItemNameEditor";
    import axios from "@/utils/axios-instance";
    import {mapState} from "vuex";


    export default {
        name: "OrderEditor",
        data() {
            return {
                isLoaded: false,
                editingOrder: null,
                orderArray: null,
                issues: null,
                order: null,
            };
        },
        components: {ItemNameEditor, QuantityPicker},
        props: ["orderId"],
        methods: {
            async loadIssues() {
                let response = await axios.get(`order/${this.orderId}/integrity-issues`);
                this.issues = response.data;
            },
            async loadOrder() {
                // Mark this order as being under editing
                let needsMarking = true;
                this.editingOrders.forEach(order => {
                    if (order.id == this.orderId) {
                        needsMarking = false;
                    }
                });

                if (needsMarking) {
                    await axios.post(`order/${this.orderId}/edit`);
                    await this.$store.dispatch('reloadEditingOrders');
                }

                let response = await axios.get(`order/${this.orderId}`);

                this.order = response.data;
                this.orderArray = this.computeOrderArray();

                await this.loadIssues();
            },
            async reloadPage() {
                let loader = this.$loading.show();

                this.isLoaded = false;

                await this.loadOrder();

                if (this.order.order.extra_data) {
                    this.form = this.order.order.extra_data;
                }

                if (!this.orderArray) {
                    this.orderArray = this.computeOrderArray();
                }

                this.isLoaded = true;
                loader.hide();
            },

            /**
             * Filter a supplier's items from the whole list.
             */
            getSupplierProducts(supplierId) {
                let filteredProducts = this.order.products.filter(product => {
                    return product.supplier_id === supplierId;
                });

                if(!this.orderArray) {
                    return [];
                }

                filteredProducts.sort((productA, productB) => {
                    let orderA = this.orderArray[productA.item_id];
                    let orderB = this.orderArray[productB.item_id];

                    return orderA - orderB;
                });

                return filteredProducts;
            },

            /**
             * Order-related API calls
             */
            async fixIssues() {
                let loader = this.$loading.show();

                await axios.post(`order/${this.orderId}/fix-integrity-issues`);
                await this.loadOrder();

                loader.hide();
            },
            async deleteOrder() {
                let loader = this.$loading.show();

                await axios.delete(`order/${this.orderId}`);
                await this.$router.push(`/saved-orders`);

                await this.$store.dispatch('reloadEditingOrders');

                loader.hide();
            },
            async saveOrder() {
                let loader = this.$loading.show();

                await axios.post(`order/${this.orderId}/save`);
                await this.$router.push(`/order/${this.orderId}/view`);

                await this.$store.dispatch('reloadEditingOrders');

                loader.hide();
            },

            /**
             * Order item-related API calls
             */

            async onItemSaveName(itemId, name) {
                let loader = this.$loading.show();

                await axios.post(`order/${this.orderId}/item/${itemId}/set-custom-name`, {
                    custom_name: name,
                });
                await this.loadOrder();

                loader.hide();
            },
            async selectItemQuantity({itemId, quantity}) {
                let loader = this.$loading.show();

                await axios.post(`order/${this.orderId}/item/${itemId}/set-quantity`, {
                    quantity: quantity,
                });
                await this.loadOrder();

                loader.hide();
            },
            async deleteItem(itemId) {
                let loader = this.$loading.show();

                await axios.delete(`order/${this.orderId}/item/${itemId}`);
                await this.loadOrder();

                if(this.order.order.status === 'EDITING') {
                    await this.$store.dispatch('reloadEditingOrders');
                }

                loader.hide();
            },

            /**
             * Order item ordering related API calls and functions
             */

            computeOrderArray(supplier_id=null) {
                let products = [];

                if(!supplier_id) {
                    products = this.order.products;
                } else {
                    products = this.getSupplierProducts(supplier_id);
                }

                if(!products || products.length === 0) {
                    return {};
                }

                let array = {};

                products.forEach(product => {
                    let {item_id, order} = product;
                    array[item_id] = order;
                });

                return array;
            },
            async changeOrder(orderArray) {
                let loader = this.$loading.show();

                await axios.post(`order/${this.orderId}/set-order`, {
                    order_array: orderArray,
                });

                await this.loadOrder();

                loader.hide();
            },
            moveItemUp(item_id, supplier_id) {
                let myOrder = this.orderArray[item_id];

                let products = this.getSupplierProducts(supplier_id);
                let supplierOrderArray = this.computeOrderArray(supplier_id);

                // Find the product with the largest order, smaller than the order of the current product.
                let bestProduct = products[0];
                products.forEach(product => {
                    let thisOrder = supplierOrderArray[product.item_id];

                    if(!bestProduct && thisOrder < myOrder) {
                        bestProduct = product;
                    }

                    if(!bestProduct) {
                        return;
                    }

                    let bestOrder = supplierOrderArray[bestProduct.item_id];

                    if(thisOrder < myOrder && thisOrder > bestOrder) {
                        bestProduct = product;
                    }
                });

                // Swap the order!
                this.orderArray[item_id] = this.orderArray[bestProduct.item_id];
                this.orderArray[bestProduct.item_id] = myOrder;

                this.changeOrder(this.orderArray);
            },
            moveItemDown(item_id, supplier_id) {
                let myOrder = this.orderArray[item_id];

                let products = this.getSupplierProducts(supplier_id);
                let supplierOrderArray = this.computeOrderArray(supplier_id);

                // Find the product with the smallest order, larger than the order of the current product.
                let bestProduct = null;
                products.forEach(product => {
                    let thisOrder = supplierOrderArray[product.item_id];

                    if(!bestProduct && thisOrder > myOrder) {
                        bestProduct = product;
                    }

                    if(!bestProduct) {
                        return;
                    }

                    let bestOrder = supplierOrderArray[bestProduct.item_id];

                    if(thisOrder > myOrder && thisOrder < bestOrder) {
                        bestProduct = product;
                    }
                });

                // Swap the order!
                this.orderArray[item_id] = this.orderArray[bestProduct.item_id];
                this.orderArray[bestProduct.item_id] = myOrder;

                this.changeOrder(this.orderArray);
            }
        },
        async created() {
            await this.reloadPage();
        },
        watch: {
            // call again the method if the route changes
            '$route': 'reloadPage'
        },
        computed: {
            hasIssues() {
                if(!this.issues) return false;

                if(this.issues.changed_order_items.length > 0) return true;
                if(this.issues.unavailable_order_items.length > 0) return true;

                return false;
            },
            ...mapState(['editingOrders']),
        }
    }
</script>

<style scoped lang="scss">
.page {
  background: white;
  border-radius: 5px;
  color: black;
  padding: 25px 20px;

  margin-top: 50px;
  margin-bottom: 20px;

  -webkit-box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);
  -moz-box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);
  box-shadow: 0 0 14px 2px rgba(0, 0, 0, 0.31);

  p {
    margin: 0;
  }

  h1 {
    color: #024A8C;
    font-weight: 600;
  }

  h2, h3, h4, h5, h6 {
    color: #6F6F6F;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  table th {
    color: #024A8C;
    font-weight: 600;
  }
}

.red.page {
  background: darkred;
  color: #e8e8e8;

  h1, h2, h3, h4, h5, h6 {
    color: #FFFFFF;
  }

  table th {
    color: #dadada;
  }

  table td {
    color: #FFFFFF;
  }
}

h1 {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}

h3 {
  font-size: 1rem;
}

table td {
  white-space: normal;
  vertical-align: middle;
  word-break: break-word;
}

table td.nowrap {
  width: 1%;
  white-space: nowrap;
}

.small-text {
  color: #6F6F6F;
  font-size: 0.7rem;
  word-wrap: break-word;
}

.product-codes {
  color: #6F6F6F;
  font-size: 0.7rem;
  word-wrap: break-word;
}

.product-codes > span::after {
  content: '; ';
}

.total {
  color: #024A8C;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.total .amount {
  font-weight: 800;
  font-size: 1.3rem;
}

.currencies div {
  margin-bottom: 5px;
}
</style>